import React from 'react'
import { css } from '@emotion/css'
import { getApplicationDrilldownID } from '../route'
import { RouteContext } from '../route-provider'
import { FlowMapLoader } from './flow-map-loader'
import { AgentFeature, AgentFeaturesLoader } from '../agent-features'
import {
  ColumnHeaderFilters,
  getAggregationFilters
} from '../../legacy-design-system/grid'
import { AggregationConfigs } from '../aggregations'
import { FlowmapStateManager } from './application-flowmap-state-manager'
import createEngine, { DiagramModel } from '@projectstorm/react-diagrams'
import { DiagramEngine } from '@projectstorm/react-diagrams-core'
import { Role } from '@contrast-security-inc/base-types'
import { ApplicationArchitecture } from './application-architecture-loader'
import { ApplicationArchitectureComponent } from './application-architecture-components-loader'
import { ArchitectureAssociatedApplication } from './architecture-associated-applications-loader'
import { ApplicationArchitectureNodeFactory } from './diagram-helpers/application-architecture-node/application-architecture-node-factory'
import { BackendSystemsNodeFactory } from './diagram-helpers/backend-systems-node/backend-systems-node-factory'
import { ConnectedApplicationsNodeFactory } from './diagram-helpers/connected-applications-node/connected-applications-node-factory'
import { CustomCanvasWidget } from './diagram-helpers/canvas-widget'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { WrappedLastAgentUpdate } from './wrapped-last-agent-update-label'
import { CustomLinkFactory } from './diagram-helpers/custom-link/custom-link-factory'
import { CustomLinkModel } from './diagram-helpers/custom-link/custom-link-model'
import {
  CustomApplicationAggregation,
  MetadataFilter
} from '../applications-grid'

import {
  AppAggregationCategory,
  componentsColumnWidth,
  FlowmapColumnType,
  architectureColumnWidth,
  connectedAppsColumnWidth,
  diagramHeight
} from './index'
import { ApplicationArchitectureNodeModel } from './diagram-helpers/application-architecture-node/application-architecture-node-model'
import { BackendSystemsNodeModel } from './diagram-helpers/backend-systems-node/backend-systems-node-model'
import { ConnectedApplicationsNodeModel } from './diagram-helpers/connected-applications-node/connected-applications-node-model'
import { appArchitecturePortOut } from './diagram-helpers/application-architecture-node'
import { buildBackendSystemPortName } from './diagram-helpers/backend-systems-node'
import { buildConnectedAppPortName } from './diagram-helpers/connected-applications-node'
import { ApplicationArchitectureColumn } from './diagram-helpers/application-architecture-node/application-architecture-column'
import { BackendSystemsColumn } from './diagram-helpers/backend-systems-node/backend-systems-column'
import { ConnectedAppsColumn } from './diagram-helpers/connected-applications-node/connected-apps-column'

const appAggregations: AppAggregationCategory[] = [
  'tags',
  'languages',
  'servers',
  'technologies',
  'environments'
]

const aggregationConfigs: AggregationConfigs<AppAggregationCategory> = {
  servers: {
    groupName: {
      id: 'title_servers'
    },
    bucketToID: ({ key: { id } }) => id,
    bucketToString: ({ key: { name } }) => name
  },
  languages: {
    groupName: {
      id: 'title_programmingLanguages'
    },
    bucketToID: ({ key: { id } }) => id,
    bucketToString: ({ key: { label } }) => label
  },
  technologies: {
    groupName: {
      id: 'title_technologies'
    }
  },
  tags: {
    groupName: {
      id: 'title_tags'
    }
  },
  environments: {
    groupName: {
      id: 'title_environments'
    },
    bucketToID: ({ key: { id } }) => id,
    bucketToString: ({ key: { label } }) => label
  }
}

interface State {
  readonly customMetadataSelections: MetadataFilter[]
}

// TODO flowmap - add tests if needed after implementing CONTRAST-37376
export class ApplicationFlowMap extends React.Component<{}, State> {
  public state: State = {
    customMetadataSelections: []
  }

  public render() {
    const { customMetadataSelections } = this.state

    return (
      <FlowmapStateManager
        filterType="applications"
        customMetadataSelections={customMetadataSelections}
      >
        {({
          onGroupToggle,
          onOptionToggle,
          aggregationSelections,
          onAggregationCategoriesClear,
          onMetadataOptionsToggle,
          customMetadataSelections,
          onClearMetadataSelections
        }) => {
          return (
            <RouteContext.Consumer>
              {({ parameters: routeParameters }) => {
                const appID = getApplicationDrilldownID(routeParameters)

                if (!appID) {
                  throw new Error('appID is required')
                }

                return (
                  <AgentFeaturesLoader
                    render={({ features = [] }) => {
                      return (
                        <FlowMapLoader
                          applicationID={appID}
                          aggregationFilters={aggregationSelections}
                          additionalFilters={
                            customMetadataSelections
                              ? { customMetadata: customMetadataSelections }
                              : {}
                          }
                        >
                          {({
                            organizationID,
                            organizationRole,
                            organizationType,
                            applicationArchitecture,
                            applicationArchitectureComponents,
                            architectureAssociatedApplications,
                            aggregations,
                            metadataAggregations = [],
                            loadingAppArchitecture,
                            loadingAppArchitectureComponents,
                            loadingAssociatedApps
                          }) => {
                            const isFreemiumOrganization =
                              organizationType === 'Freemium'

                            const filters:
                              | ColumnHeaderFilters<AppAggregationCategory>
                              | undefined = !!aggregations
                              ? {
                                  groupConfigs: getAggregationFilters({
                                    aggregationConfigs,
                                    aggregations
                                  }),
                                  selections: aggregationSelections,
                                  groupIDs: appAggregations,
                                  onGroupToggle,
                                  onOptionToggle
                                }
                              : undefined

                            return (
                              <FlowMapManager<AppAggregationCategory>
                                architecture={applicationArchitecture}
                                components={applicationArchitectureComponents}
                                connectedApps={
                                  architectureAssociatedApplications
                                }
                                loadingArchitecture={loadingAppArchitecture}
                                loadingComponents={
                                  loadingAppArchitectureComponents
                                }
                                loadingConnectedApps={loadingAssociatedApps}
                                appID={appID}
                                organizationID={organizationID}
                                organizationRole={organizationRole}
                                features={features}
                                isFreemiumOrganization={isFreemiumOrganization}
                                onAggregationCategoriesClear={
                                  onAggregationCategoriesClear
                                }
                                filters={filters}
                                metadataAggregations={metadataAggregations}
                                customMetadataSelections={
                                  customMetadataSelections
                                }
                                onMetadataOptionsToggle={
                                  onMetadataOptionsToggle
                                }
                                onClearMetadataSelections={
                                  onClearMetadataSelections
                                }
                              />
                            )
                          }}
                        </FlowMapLoader>
                      )
                    }}
                  />
                )
              }}
            </RouteContext.Consumer>
          )
        }}
      </FlowmapStateManager>
    )
  }
}

interface FlowMapManagerProps<TAggregationCategory extends string> {
  readonly architecture?: ApplicationArchitecture
  readonly components: ApplicationArchitectureComponent[]
  readonly connectedApps: ArchitectureAssociatedApplication[]
  readonly loadingArchitecture: boolean
  readonly loadingComponents: boolean
  readonly loadingConnectedApps: boolean
  readonly appID: string
  readonly organizationID: string
  readonly organizationRole: Role
  readonly features: AgentFeature[]
  readonly isFreemiumOrganization: boolean
  readonly onAggregationCategoriesClear: (
    categories: TAggregationCategory[]
  ) => void
  readonly filters: ColumnHeaderFilters<AppAggregationCategory> | undefined
  readonly metadataAggregations: CustomApplicationAggregation[]
  readonly onMetadataOptionsToggle: (
    groupID: string,
    optionIDs: string[]
  ) => void
  readonly customMetadataSelections: MetadataFilter[]
  readonly onClearMetadataSelections: () => void
}

export class FlowMapManager<
  TAggregationCategory extends string
> extends React.Component<FlowMapManagerProps<TAggregationCategory>> {
  public render() {
    const {
      architecture,
      components,
      connectedApps,
      loadingArchitecture,
      loadingComponents,
      loadingConnectedApps,
      filters,
      customMetadataSelections,
      appID,
      organizationID,
      organizationRole,
      features,
      isFreemiumOrganization,
      onClearMetadataSelections,
      onMetadataOptionsToggle,
      metadataAggregations,
      onAggregationCategoriesClear
    } = this.props

    return (
      <div
        className={css`
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
        `}
      >
        <div
          className={css`
            justify-content: flex-end;
            display: flex;
            margin-bottom: 20px;
            min-height: 17px;
            width: ${architectureColumnWidth +
            componentsColumnWidth +
            connectedAppsColumnWidth}px;
          `}
        >
          <WrappedLastAgentUpdate
            organizationID={organizationID}
            applicationID={appID}
          />
        </div>

        <div
          className={css`
            display: flex;
            flex-direction: row;
          `}
        >
          <ApplicationArchitectureColumn
            appID={appID}
            organizationID={organizationID}
            isLoading={loadingArchitecture}
            appArchitecture={architecture}
            organizationRole={organizationRole}
            features={features}
            isFreemiumOrganization={isFreemiumOrganization}
          />
          <div
            className={css`
              display: flex;
              flex: 1;
              flex-direction: column;

              height: auto;
            `}
          >
            <div
              className={css`
                display: flex;
                flex-direction: row;
              `}
            >
              <BackendSystemsColumn />
              <ConnectedAppsColumn
                filters={filters}
                metadataAggregations={metadataAggregations}
                customMetadataSelections={customMetadataSelections}
                onMetadataOptionsToggle={onMetadataOptionsToggle}
                onClearMetadataSelections={onClearMetadataSelections}
                appAggregations={appAggregations}
                getOnAllAggregationCategoriesClear={this.getOnAllAggregationCategoriesClear(
                  onAggregationCategoriesClear,
                  onClearMetadataSelections
                )}
              />
            </div>

            <FlowMapDiagram
              components={components}
              connectedApps={connectedApps}
              loadingComponents={loadingComponents}
              loadingConnectedApps={loadingConnectedApps}
              appID={appID}
              organizationID={organizationID}
              organizationRole={organizationRole}
              features={features}
              isFreemiumOrganization={isFreemiumOrganization}
            />
          </div>
        </div>
      </div>
    )
  }

  private getOnAllAggregationCategoriesClear =
    (onAggregationCategoriesClear, onClearMetadataSelections) =>
    (categories: AppAggregationCategory[]) => {
      if (
        categories.every((category) => appAggregations.indexOf(category) > -1)
      ) {
        onClearMetadataSelections()
      }
      onAggregationCategoriesClear(categories)
    }
}

interface FlowMapDiagramProps {
  readonly components: ApplicationArchitectureComponent[]
  readonly connectedApps: ArchitectureAssociatedApplication[]

  readonly loadingComponents: boolean
  readonly loadingConnectedApps: boolean

  readonly appID: string
  readonly organizationID: string
  readonly organizationRole: Role
  readonly features: AgentFeature[]
  readonly isFreemiumOrganization: boolean
}

interface FlowMapDiagramState {
  readonly selectedComponentHash?: string
}

export class FlowMapDiagram extends React.Component<
  FlowMapDiagramProps,
  FlowMapDiagramState
> {
  private engine: DiagramEngine

  private architectureNode: ApplicationArchitectureNodeModel
  private componentsNode: BackendSystemsNodeModel
  private connectedAppsNode: ConnectedApplicationsNodeModel

  private startIndexComponents: number = 0
  private stopIndexComponents: number = 0
  private startIndexConnectedApps: number = 0
  private stopIndexConnectedApps: number = 0

  public constructor(props: FlowMapDiagramProps) {
    super(props)

    this.state = {
      selectedComponentHash: undefined
    }

    this.initDiagramNodes()
    this.initDiagramEngine()
  }

  public render() {
    this.updateColumnsData()

    return (
      <div
        className={css`
          height: ${diagramHeight}px;
          display: flex;

          > * {
            height: 100%;
            min-height: 100%;

            width: 100%;
          }
        `}
      >
        <CustomCanvasWidget
          className={css`
            position: relative;
            overflow: hidden;
          `}
          engine={this.engine}
        />
      </div>
    )
  }

  private updateColumnsData() {
    const {
      components,
      connectedApps,
      loadingComponents,
      loadingConnectedApps
    } = this.props

    this.componentsNode.items = components
    this.componentsNode.isLoading = loadingComponents
    this.connectedAppsNode.items = connectedApps
    this.connectedAppsNode.isLoading = loadingComponents || loadingConnectedApps

    this.stopIndexComponents =
      this.stopIndexComponents > components.length
        ? components.length - 1
        : this.stopIndexComponents

    this.stopIndexConnectedApps =
      this.stopIndexConnectedApps > connectedApps.length
        ? connectedApps.length - 1
        : this.stopIndexConnectedApps

    this.generateNodeLinks()
  }

  private initDiagramNodes = () => {
    const {
      components,
      connectedApps,
      loadingComponents,
      loadingConnectedApps,
      organizationID,
      features,
      isFreemiumOrganization
    } = this.props

    /* 1st column - application architecture */
    this.architectureNode = new ApplicationArchitectureNodeModel()
    this.architectureNode.setPosition(0, 0)

    /* 2nd column - back-end systems */
    this.componentsNode = new BackendSystemsNodeModel({
      onRowsRendered: this.onRowsRendered,
      onClickComponent: this.onClickComponent,
      items: components,
      isLoading: loadingComponents
    })
    this.componentsNode.setPosition(0, 0)

    /* 3rd column - connected apps */
    this.connectedAppsNode = new ConnectedApplicationsNodeModel({
      onRowsRendered: this.onRowsRendered,
      items: connectedApps,
      organizationID,
      features,
      isFreemiumOrganization,
      isLoading: loadingComponents || loadingConnectedApps
    })
    this.connectedAppsNode.setPosition(componentsColumnWidth, 0)
  }

  private initDiagramEngine = () => {
    this.engine = createEngine()
    const model = new DiagramModel()
    model.setLocked(true)

    /* init node/link factories */
    this.engine.getLinkFactories().registerFactory(new CustomLinkFactory())

    this.engine
      .getNodeFactories()
      .registerFactory(new ApplicationArchitectureNodeFactory())
    this.engine
      .getNodeFactories()
      .registerFactory(new BackendSystemsNodeFactory())

    model.addAll(this.architectureNode, this.componentsNode)
    this.engine
      .getNodeFactories()
      .registerFactory(new ConnectedApplicationsNodeFactory())

    model.addAll(this.connectedAppsNode)

    this.engine.setModel(model)
  }

  private onRowsRendered = (
    startIndex: number,
    stopIndex: number,
    type: FlowmapColumnType
  ) => {
    // TODO
    // console.log(`onRowsRendered - ${type}: ${startIndex} - ${stopIndex}`)

    if (type === 'COMPONENTS') {
      this.startIndexComponents = startIndex
      this.stopIndexComponents = stopIndex
    } else if (type === 'CONNECTED_APPS') {
      this.startIndexConnectedApps = startIndex
      this.stopIndexConnectedApps = stopIndex
    }

    this.forceUpdate()
  }

  private generateNodeLinks() {
    const { components, connectedApps } = this.props
    // create links only for visible items
    const model = this.engine.getModel()

    this.componentsNode.removePortsAndLinks()
    if (components.length > 0) {
      this.componentsNode.createNewPorts(
        this.startIndexComponents,
        this.stopIndexComponents
      )
    }
    this.connectedAppsNode.removePortsAndLinks()
    if (connectedApps.length > 0) {
      this.connectedAppsNode.createNewPorts(
        this.startIndexConnectedApps,
        this.stopIndexConnectedApps
      )
    }

    model.addAll(...this.createNodeLinks())
  }

  private onClickComponent = (hash: string) => {
    this.setState(
      (prevState) => ({
        selectedComponentHash:
          prevState.selectedComponentHash === hash ? undefined : hash
      }),
      () => this.forceUpdate()
    )
  }

  private createNodeLinks(): CustomLinkModel[] {
    // TODO
    /*
    console.log(`this.startIndexComponents: ${this.startIndexComponents}`)
    console.log(`this.stopIndexComponents: ${this.stopIndexComponents}`)
    console.log(`this.startIndexConnectedApps: ${this.startIndexConnectedApps}`)
    console.log(`this.stopIndexConnectedApps: ${this.stopIndexConnectedApps}`)*/

    const { components, connectedApps } = this.props
    const { selectedComponentHash } = this.state
    const links: CustomLinkModel[] = []

    // no components, no links
    if (components.length === 0) {
      return []
    }

    const architecturePortOut = this.architectureNode.getPort(
      appArchitecturePortOut
    )

    // only visible items
    for (
      let componentIndex = this.startIndexComponents;
      componentIndex <= this.stopIndexComponents;
      componentIndex++
    ) {
      const component = components[componentIndex]
      const dashedLink = component.encryptionStatus !== 'ENCRYPTED'

      /* connects
       * architecture
       * with
       * components
       */
      const portInName = buildBackendSystemPortName(componentIndex, 'in')
      const componentPortIn = this.componentsNode.getPort(portInName)

      if (architecturePortOut && componentPortIn) {
        const link = this.createLink(dashedLink, false, 'COMPONENTS')

        link.setSourcePort(architecturePortOut)
        link.setTargetPort(componentPortIn)
        links.push(link)
      }

      /* connects
       * components
       * with
       * connected apps
       */
      // no connectedApps, no links between 2nd and 3rd columns
      if (connectedApps.length > 0) {
        const componentPortOutName = buildBackendSystemPortName(
          componentIndex,
          'out'
        )
        const componentPortOut =
          this.componentsNode.getPort(componentPortOutName)

        for (
          let connectedAppIndex = this.startIndexConnectedApps;
          connectedAppIndex <= this.stopIndexConnectedApps;
          connectedAppIndex++
        ) {
          const { componentHashes: connectedAppComponentHashes } =
            connectedApps[connectedAppIndex]

          const portInName = buildConnectedAppPortName(connectedAppIndex, 'in')
          const connectedAppPortIn = this.connectedAppsNode.getPort(portInName)

          const isConnected = connectedAppComponentHashes.some(
            (connectedAppComponentHash) =>
              component.hash === connectedAppComponentHash
          )

          if (isConnected && componentPortOut && connectedAppPortIn) {
            const isSelected = selectedComponentHash === component.hash

            const link = this.createLink(
              dashedLink,
              isSelected,
              'CONNECTED_APPS'
            )

            link.setSourcePort(componentPortOut)
            link.setTargetPort(connectedAppPortIn)
            links.push(link)
          }
        }
      }
    }
    return links
  }

  private createLink(
    dashed: boolean,
    isSelected: boolean,
    columnType: FlowmapColumnType
  ): CustomLinkModel {
    return new CustomLinkModel({
      color: isSelected ? theme.color.purple70 : theme.color.neutral60,
      width: 1,
      dashed,
      columnType
    })
  }
}
